import { useEffect, useMemo } from 'react';
import { useFormContext } from 'react-hook-form';

import { Schema, ValuesOf } from '@typings';
import { CUSTOM_COMPONENT_NAME } from '@constants';
import { as, isNumber, normalizePlatformAppsFormErrors } from '@utils';

import { JobPresetField } from '@components/Job';
import { Field } from '@components/Shared';

import {
  SchemaEnvironments,
  SchemaImage,
  SchemaSecret,
  SchemaStoragePath,
  SchemaTags,
  SchemaVolumes,
} from './components';
import { SchemaArray } from './SchemaArray';

/**
 * Formatter fixes case with `anyOf` schemas.
 * When `anyOf` state is changed, form parsed value is set to `null` on register.
 * That triggers validation error for primitive types
 */
const formatter = {
  number: (value: any) => {
    const normalizedValue = Number(value);
    const isEmpty = value === '' || value === null;

    return isNumber(normalizedValue) &&
      !isEmpty &&
      !Number.isNaN(normalizedValue)
      ? normalizedValue
      : undefined;
  },
  string: (value: any = '') => (value === null ? '' : value),
  boolean: (value: any) => (value === null ? false : value),
};

const CONSISTENT_CUSTOM_DEF_COMPONENTS = {
  PRESET: {
    component: JobPresetField,
    keys: CUSTOM_COMPONENT_NAME.PRESET,
  },
  SECRET: {
    component: SchemaSecret,
    keys: CUSTOM_COMPONENT_NAME.SECRET,
  },
  STORAGE_PATH: {
    component: SchemaStoragePath,
    keys: CUSTOM_COMPONENT_NAME.STORAGE_PATH,
  },
  IMAGE: {
    component: SchemaImage,
    keys: CUSTOM_COMPONENT_NAME.IMAGE,
  },
  TAGS: {
    component: SchemaTags,
    keys: CUSTOM_COMPONENT_NAME.TAGS,
  },
};

/**
 * Used only for root properties
 */
const CONSISTENT_CUSTOM_ROOT_COMPONENTS = {
  env: SchemaEnvironments,
  volumes: SchemaVolumes,
} as const;

type Props = {
  name: string;
  properties: Schema.FieldProperties;
  withFormatter?: boolean;
  typeName?: string;
  isTopLevelNode?: boolean;
};

export const SchemaField: React.FC<Props> = (props) => {
  const {
    name,
    typeName,
    isTopLevelNode,
    withFormatter,
    properties: {
      title,
      description,
      required,
      type,
      options,
      items,
      const: constant,
    },
  } = props;
  const { register, formState, setValue } = useFormContext();

  const isConstant = constant !== undefined;
  const { [name]: error } = normalizePlatformAppsFormErrors(formState.errors);
  const fieldName = name.split('.').at(-1);

  const getFormatter = useMemo(
    () => (formatFn: ValuesOf<typeof formatter>) =>
      withFormatter ? formatFn : undefined,
    [withFormatter],
  );

  const [Component, additionalProps, children] = (() => {
    const customDefComponent = Object.values(
      CONSISTENT_CUSTOM_DEF_COMPONENTS,
    ).find(
      ({ keys: [customTypeName, customFieldName] }) =>
        customTypeName === typeName && customFieldName === fieldName,
    );

    if (customDefComponent) {
      return [customDefComponent.component];
    }

    const customRootComponent =
      CONSISTENT_CUSTOM_ROOT_COMPONENTS[
        name as keyof typeof CONSISTENT_CUSTOM_ROOT_COMPONENTS
      ];

    if (isTopLevelNode && customRootComponent) {
      return [customRootComponent];
    }

    switch (type) {
      case 'boolean':
        return [
          Field.Checkbox,
          register(name, { setValueAs: getFormatter(formatter.boolean) }),
          title,
        ];

      case 'string':
        return [
          Field.Input,
          register(name, { setValueAs: getFormatter(formatter.string) }),
        ];

      case 'array':
        return [SchemaArray, { items }];

      case 'enum': {
        const selectOptions = as
          .a<string[]>(options)
          .map((value) => ({ value, text: value }));

        return [Field.Select, { ...register(name), options: selectOptions }];
      }

      case 'integer':
      case 'number':
      case 'float': {
        const options = withFormatter
          ? { setValueAs: getFormatter(formatter.number) }
          : { valueAsNumber: true };

        return [
          Field.Input,
          {
            ...register(name, options),
            type: 'number',
            inputMode:
              'numeric' as React.HTMLAttributes<HTMLInputElement>['inputMode'],
          },
        ];
      }

      default:
        return [null];
    }
  })();

  useEffect(() => {
    if (isConstant && name) {
      register(name, { value: constant });
      setValue(name, constant);
    }
  }, [name, isConstant, constant, register, setValue]);

  if (isConstant || !Component) {
    return null;
  }

  return (
    <Component
      name={name}
      error={error}
      required={required}
      label={title}
      note={description}
      {...additionalProps}
    >
      {children}
    </Component>
  );
};
